import type { AddFamiliesRequest, FamilyGroup } from "@teamna/shared";
import { useState } from "react";
import {
  Button,
  ChipRow,
  CloseIcon,
  Field,
  Panel,
  SelectField,
  TextAreaField,
  showToast,
} from "../../components/index.ts";
import { toAge } from "./editors.tsx";
import { capital, firstName, householdWord, parseFamilyList, SAMPLE_LIST, type NewFamily, type Words } from "./model.ts";
import type { FamilyActions } from "./types.ts";

/**
 * "Add families": some now and more later. Three ways in, one result: the families on the list,
 * each child in the group chosen for the whole list, and Undo for the whole list. A number that is
 * already at the business is not added twice. A WhatsApp group comes in through the WhatsApp sync
 * tool, not here (the chat export file was removed on 2026-10-06).
 *
 * Where clients book for themselves (F2) the panel adds clients: a name and a number for each, with
 * no children, no second adult and no group to put children in.
 */

export type AddSource = "paste" | "csv" | "one";

type Draft = { adults: Array<{ key: number; name: string; phone: string }>; children: Array<{ key: number; name: string; age: string }> };
let draftKey = 0;
const nextKey = () => (draftKey += 1);
const emptyDraft = (): Draft => ({ adults: [{ key: nextKey(), name: "", phone: "" }], children: [{ key: nextKey(), name: "", age: "" }] });

export function AddFamiliesPanel({
  open,
  onClose,
  groups,
  words,
  actions,
  initialGroup = "none",
  initialSource = "paste",
  onOpenCsv,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  groups: FamilyGroup[];
  words: Words;
  actions: FamilyActions;
  initialGroup?: string;
  initialSource?: AddSource;
  /** Opens the spreadsheet import, which maps the columns before anything is saved. */
  onOpenCsv: () => void;
  onError: (error: unknown) => void;
}) {
  const [source, setSource] = useState<AddSource>(initialSource);
  const [target, setTarget] = useState(initialGroup);
  const [pasted, setPasted] = useState("");
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [busy, setBusy] = useState(false);

  const groupName = target === "none" ? null : target === "unsure" ? "Not sure yet" : (groups.find((group) => group.id === target)?.name ?? null);

  const forChildren = words.forChildren;
  const parsed: NewFamily[] =
    source === "paste"
      ? parseFamilyList(pasted)
      : source === "one"
        ? (() => {
            const adults = draft.adults.filter((adult) => adult.name.trim() || adult.phone.trim()).map((adult) => ({ name: adult.name.trim(), phone: adult.phone.trim() }));
            if (adults.length === 0) return [];
            return [{ adults, children: draft.children.filter((child) => child.name.trim()).map((child) => ({ name: child.name.trim(), age: toAge(child.age) })) }];
          })()
        : [];
  // A client books for themselves: a list line or a form never adds a child.
  const incoming = forChildren ? parsed : parsed.map((family) => ({ adults: family.adults.slice(0, 1), children: [] }));
  const childCount = incoming.reduce((sum, family) => sum + family.children.length, 0);

  async function commit() {
    if (incoming.length === 0) return;
    setBusy(true);
    const request: AddFamiliesRequest = {
      source: "pasted",
      families: incoming.map((family) => ({
        adults: family.adults.map((adult) => ({ name: adult.name, ...(adult.phone ? { phone: adult.phone } : {}) })),
        children: family.children.map((child) => ({ name: child.name, age: child.age })),
      })),
      ...(forChildren && target !== "none" && target !== "unsure" ? { groupId: target } : {}),
      ...(forChildren && target === "unsure" ? { unsure: true } : {}),
    };
    try {
      const result = await actions.addFamilies(request);
      onClose();
      setPasted("");
      setDraft(emptyDraft());
      const added = `${result.added} ${householdWord(result.added, words)} added`;
      const where = forChildren && groupName && result.added ? `, children in ${groupName}` : "";
      const twice = result.alreadyHere ? ` ${result.alreadyHere} already here, not added again.` : "";
      const unread = result.unreadable ? ` ${result.unreadable} ${result.unreadable === 1 ? "number" : "numbers"} we could not read: added with no number.` : "";
      showToast(`${added}${where}.${twice}${unread}`, {
        ...(result.added
          ? { action: { label: "Undo", onClick: () => void actions.undoFamilies(result.batch).catch(onError) } }
          : { tone: "info" as const }),
      });
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  const commitLabel =
    source === "csv"
      ? "Open the spreadsheet import"
      : incoming.length
        ? `Add ${incoming.length} ${householdWord(incoming.length, words)}`
        : "Add";

  return (
    <Panel
      open={open}
      onClose={onClose}
      title={`Add ${householdWord(2, words)}`}
      subtitle="Add some now and more later. A number that is already here is not added twice."
      width="lg"
      actions={
        source === "csv" ? (
          <Button onClick={onOpenCsv}>{commitLabel}</Button>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void commit()} disabled={incoming.length === 0} loading={busy}>
              {commitLabel}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <ChipRow
          label="Where the list comes from"
          selectedId={source}
          onSelect={(id) => setSource(id as AddSource)}
          wrap
          options={[
            { id: "paste", label: "Paste a list" },
            { id: "csv", label: "Spreadsheet" },
            { id: "one", label: `One ${householdWord(1, words)}` },
          ]}
        />

        {source !== "csv" && forChildren ? (
          <SelectField
            label="Put the children from this list in"
            hint={`Use this for a list of one ${words.group}.`}
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          >
            <option value="none">No {words.group} yet</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
            <option value="unsure">Not sure yet</option>
          </SelectField>
        ) : null}

        {source === "paste" ? (
          <div className="flex flex-col gap-2">
            <TextAreaField
              label={`One ${householdWord(1, words)} on each line`}
              hint={
                forChildren
                  ? `${capital(words.member)} and number, then a colon and the children. Two ${words.members}: join them with &. The age is optional.`
                  : "The name and the mobile number."
              }
              rows={7}
              value={pasted}
              onChange={(event) => setPasted(event.target.value)}
              placeholder={forChildren ? "Huda Saleh 050 555 1010 & Faisal Saleh 055 555 2020: Mira 6, Ali 9" : "Huda Saleh 050 555 1010"}
              dir="auto"
            />
            {forChildren ? (
              <div>
                <Button variant="ghost" onClick={() => setPasted(SAMPLE_LIST)}>
                  Fill with an example
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {source === "csv" ? (
          <div className="rounded-card bg-surface p-4 text-[14px] leading-relaxed text-ink-2">
            {forChildren
              ? `A spreadsheet can carry the ${words.member}, the number, the child, the ${words.group}, the sessions left and paid or owed.`
              : `A spreadsheet can carry the ${words.member}, the number, the sessions left and paid or owed.`}{" "}
            You check how the columns match before anything is saved.
          </div>
        ) : null}

        {source === "one" ? (
          <div className={`grid gap-5 ${forChildren ? "sm:grid-cols-2" : ""}`}>
            <div className="flex flex-col gap-2">
              <p className="eyebrow text-ink-2">{capital(words.members)}</p>
              {draft.adults.map((adult, index) => (
                <div key={adult.key} className="flex items-end gap-2">
                  <Field
                    size="compact"
                    label={index === 0 ? `${capital(words.member)} name` : "Second adult"}
                    value={adult.name}
                    onChange={(event) => setDraft({ ...draft, adults: draft.adults.map((row, i) => (i === index ? { ...row, name: event.target.value } : row)) })}
                    dir="auto"
                  />
                  <Field
                    size="compact"
                    label="Mobile number"
                    type="tel"
                    inputMode="tel"
                    placeholder="050 123 4567"
                    value={adult.phone}
                    onChange={(event) => setDraft({ ...draft, adults: draft.adults.map((row, i) => (i === index ? { ...row, phone: event.target.value } : row)) })}
                  />
                  {index > 0 ? (
                    <RemoveRow label={`Remove ${adult.name || "this adult"}`} onClick={() => setDraft({ ...draft, adults: draft.adults.filter((_, i) => i !== index) })} />
                  ) : null}
                </div>
              ))}
              {forChildren && draft.adults.length < 2 ? (
                <div>
                  <Button variant="ghost" onClick={() => setDraft({ ...draft, adults: [...draft.adults, { key: nextKey(), name: "", phone: "" }] })}>
                    Add another {words.member}
                  </Button>
                </div>
              ) : null}
            </div>
            {forChildren ? (
              <div className="flex flex-col gap-2">
                <p className="eyebrow text-ink-2">Children</p>
                {draft.children.map((child, index) => (
                  <div key={child.key} className="flex items-end gap-2">
                    <Field
                      size="compact"
                      label="Child's name"
                      value={child.name}
                      onChange={(event) => setDraft({ ...draft, children: draft.children.map((row, i) => (i === index ? { ...row, name: event.target.value } : row)) })}
                      dir="auto"
                    />
                    <Field
                      size="compact"
                      label="Age, optional"
                      inputMode="numeric"
                      value={child.age}
                      onChange={(event) => setDraft({ ...draft, children: draft.children.map((row, i) => (i === index ? { ...row, age: event.target.value } : row)) })}
                      className="w-[96px]"
                    />
                    {draft.children.length > 1 ? (
                      <RemoveRow label={`Remove ${child.name || "this child"}`} onClick={() => setDraft({ ...draft, children: draft.children.filter((_, i) => i !== index) })} />
                    ) : null}
                  </div>
                ))}
                <div>
                  <Button variant="ghost" onClick={() => setDraft({ ...draft, children: [...draft.children, { key: nextKey(), name: "", age: "" }] })}>
                    Add another child
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {source !== "csv" ? (
          <p className="text-[13px] text-ink-2" aria-live="polite">
            {incoming.length
              ? (forChildren
                  ? `${incoming.length} ${householdWord(incoming.length, words)}, ${childCount} ${childCount === 1 ? "child" : "children"}${groupName ? `, all in ${groupName}` : `, no ${words.group} yet`}. `
                  : `${incoming.length} ${householdWord(incoming.length, words)}. `) +
                (incoming.some((family) => family.adults.some((adult) => !adult.phone)) ? "Some have no number: you can add it later. " : "") +
                "You can undo the whole list."
              : "You can undo the whole list after you add it."}
            {source === "paste" && incoming[0] ? ` First: ${incoming[0].adults.map((adult) => firstName(adult.name)).join(" and ")}.` : ""}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

function RemoveRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex size-10 shrink-0 items-center justify-center rounded-lg text-ink-2 hover:bg-sunken hover:text-ink"
    >
      <CloseIcon size={16} />
    </button>
  );
}
